import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { createSupportReport } from '../../api/supportApi';
import { toast } from 'react-toastify';
import { SUPPORT_FIELD_LIMITS, SUPPORT_FILE_CATEGORIES } from '../../utils/supportAccess';

const SupportFormModal = ({
  isOpen,
  onClose,
  onCreated,
  initialType = 'bug',
  initialTitle = '',
  initialDescription = '',
}) => {
  const location = useLocation();
  const submitLock = useRef(false);
  const [submitError, setSubmitError] = useState('');
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    type: initialType,
    route: '',
    app: 'web',
    browser: '',
    platform: '',
    screenSize: '',
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Detect browser info when component mounts
  useEffect(() => {
    if (!isOpen) return;
    setSubmitError('');
    setFormData(prev => ({
      ...prev,
      title: initialTitle || '',
      description: initialDescription || '',
      type: SUPPORT_FILE_CATEGORIES.some((option) => option.value === initialType) ? initialType : 'bug',
      route: location.pathname,
      browser: detectBrowser(),
      platform: detectPlatform(),
      screenSize: `${window.innerWidth}x${window.innerHeight}`,
    }));
  }, [isOpen, initialType, initialTitle, initialDescription, location.pathname]);

  // Detect browser
  const detectBrowser = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Chrome') > -1) return 'Chrome';
    if (userAgent.indexOf('Safari') > -1) return 'Safari';
    if (userAgent.indexOf('Firefox') > -1) return 'Firefox';
    if (userAgent.indexOf('MSIE') > -1 || userAgent.indexOf('Trident') > -1) return 'IE';
    if (userAgent.indexOf('Edge') > -1) return 'Edge';
    return 'Unknown';
  };

  // Detect platform
  const detectPlatform = () => {
    const userAgent = navigator.userAgent;
    if (userAgent.indexOf('Win') > -1) return 'Windows';
    if (userAgent.indexOf('Mac') > -1) return 'MacOS';
    if (userAgent.indexOf('Linux') > -1) return 'Linux';
    if (userAgent.indexOf('Android') > -1) return 'Android';
    if (userAgent.indexOf('iPhone') > -1 || userAgent.indexOf('iPad') > -1) return 'iOS';
    return 'Unknown';
  };

  const handleChange = e => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = async e => {
    e.preventDefault();
    if (submitLock.current) return;
    submitLock.current = true;
    setIsSubmitting(true);
    setSubmitError('');

    try {
      // tenantId/userId are derived from the auth token on the server
      const reportData = {
        ...formData,
        title: formData.title.trim(),
        description: formData.description.trim(),
        status: 'open',
      };

      const created = await createSupportReport(reportData);
      toast.success('Your support request was submitted.');
      if (onCreated) onCreated(created);
      onClose();
    } catch (error) {
      console.error('Error submitting report:', error);
      const message = error?.message || 'Failed to submit report. Please try again.';
      setSubmitError(message);
      toast.error(message);
    } finally {
      submitLock.current = false;
      setIsSubmitting(false);
    }
  };

  // If modal is not open, don't render anything
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-4 pt-4 pb-20 text-center sm:block sm:p-0">
        {/* Background overlay */}
        <div
          className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75"
          onClick={onClose}
          aria-hidden="true"
        ></div>

        {/* Modal panel */}
        <div className="inline-block overflow-hidden text-left align-bottom transition-all transform bg-white rounded-lg shadow-xl sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
          <div className="px-4 pt-5 pb-4 bg-white sm:p-6 sm:pb-4">
            <div className="sm:flex sm:items-start">
              <div className="mt-3 text-center sm:mt-0 sm:ml-4 sm:text-left w-full">
                <h3 className="text-lg font-medium leading-6 text-gray-900">
                  Report a Problem
                </h3>

                <form onSubmit={handleSubmit} className="mt-4">
                  {/* Title */}
                  <div className="mb-4">
                    <label htmlFor="title" className="block text-sm font-medium text-gray-700">
                      Subject
                    </label>
                    <input
                      type="text"
                      id="title"
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      maxLength={SUPPORT_FIELD_LIMITS.title}
                      className="block w-full mt-1 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm border-gray-800 p-2"
                      required
                    />
                  </div>

                  {/* Type */}
                  <div className="mb-4">
                    <label htmlFor="type" className="block text-sm font-medium text-gray-700">
                      Category
                    </label>
                    <select
                      id="type"
                      name="type"
                      value={formData.type}
                      onChange={handleChange}
                      className="block w-full mt-1 border border-gray-800 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2"
                    >
                      {SUPPORT_FILE_CATEGORIES.map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </select>
                  </div>

                  {/* Description */}
                  <div className="mb-4">
                    <label
                      htmlFor="description"
                      className="block text-sm font-medium text-gray-700"
                    >
                      Description
                    </label>
                    <textarea
                      id="description"
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      rows={4}
                      maxLength={SUPPORT_FIELD_LIMITS.description}
                      className="block w-full mt-1 border border-gray-800 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 sm:text-sm p-2"
                      required
                    ></textarea>
                  </div>

                  {submitError ? (
                    <p className="mb-4 text-sm text-red-700" role="alert">
                      {submitError}
                    </p>
                  ) : null}

                  {/* System info - shown but disabled */}
                  <div className="mb-4 p-3 bg-gray-50 rounded-md">
                    <p className="text-xs text-gray-500 mb-2">
                      System info (automatically included):
                    </p>
                    <div className="grid grid-cols-2 gap-2 text-xs text-gray-600">
                      <div>Route: {formData.route}</div>
                      <div>Browser: {formData.browser}</div>
                      <div>Platform: {formData.platform}</div>
                      <div>Screen: {formData.screenSize}</div>
                    </div>
                  </div>

                  {/* Buttons */}
                  <div className="mt-5 sm:mt-4 sm:flex sm:flex-row-reverse">
                    <button
                      type="submit"
                      disabled={isSubmitting}
                      className="inline-flex justify-center w-full px-4 py-2 text-base font-medium text-white bg-blue-600 border border-transparent rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {isSubmitting ? 'Submitting...' : 'Submit'}
                    </button>
                    <button
                      type="button"
                      onClick={onClose}
                      className="inline-flex justify-center w-full px-4 py-2 mt-3 text-base font-medium text-gray-700 bg-white border border-gray-300 rounded-md shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:mt-0 sm:w-auto sm:text-sm"
                    >
                      Cancel
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SupportFormModal;
